import type { StipulationListItem, StipulationListResponse } from "@credit-acceptance/step6-bff-client";

import { toStatusDisplay } from "./statusMap";
import type { StipulationActionView, StipulationHistoryView, StipulationListView, StipulationView } from "./types";

/**
 * Maps a generated `AvailableAction` to the action view, keeping only the fields
 * the action menu needs and normalizing the optional `reasons` submenu.
 * @param {NonNullable<StipulationListItem["availableActions"]>[number]} action - Generated action.
 * @returns {StipulationActionView} The panel-ready action.
 */
export function mapAvailableAction(
  action: NonNullable<StipulationListItem["availableActions"]>[number]
): StipulationActionView {
  return {
    actionCode: action.actionCode ?? "",
    displayName: action.displayName ?? action.actionCode ?? "",
    requiresNote: action.requiresNote ?? false,
    reasons: (action.reasons ?? []).map((reason) => ({
      reasonPk: reason.reasonPk,
      displayName: reason.displayName ?? "",
    })),
  };
}

/**
 * Maps a generated `ActionHistoryEntry` to the history view.
 * @param {NonNullable<StipulationListItem["history"]>[number]} entry - Generated history entry.
 * @returns {StipulationHistoryView} The panel-ready history entry.
 */
function mapHistory(entry: NonNullable<StipulationListItem["history"]>[number]): StipulationHistoryView {
  return {
    actionCode: entry.actionCode,
    reasonDescription: entry.reasonDescription,
    noteText: entry.noteText,
    performedBy: entry.performedBy,
    performedAt: entry.performedAt,
  };
}

/**
 * Maps a single generated `StipulationListItem` to the panel view. The BFF item
 * has no `id`; compose one from `stipCode` + `applicantPk` so Accordion keys stay
 * unique when the same stip applies to multiple applicants. When both are absent
 * the composite is empty, so fall back to the array index to keep keys unique.
 * `title` falls back to `stipCode` when the server omits it.
 * @param {StipulationListItem} item - Generated item.
 * @param {number} index - Position in the list, used as an id fallback.
 * @returns {StipulationView} The panel-ready stipulation.
 */
export function mapStipulationItem(item: StipulationListItem, index: number): StipulationView {
  const stipCode = item.stipCode ?? "";
  const composite = `${stipCode}:${item.applicantPk ?? ""}`;
  const stipulationAppliedId = item.stipulationAppliedId;
  return {
    id: composite === ":" ? `stip-${index}` : composite,
    stipCode,
    stipulationAppliedId,
    title: item.title ?? stipCode,
    status: item.status ?? "",
    statusDisplay: toStatusDisplay(item.status),
    categoryCode: item.categoryCode,
    description: item.description,
    policyTag: item.policyTag,
    currentAction: item.currentAction,
    openLoanStip: item.openLoanStip,
    applicantPk: item.applicantPk,
    workbenchScreens: item.workbenchScreens,
    availableActions: (item.availableActions ?? []).map((action) => mapAvailableAction(action)),
    recentNote: item.recentNote
      ? {
          actionName: item.recentNote.actionName,
          description: item.recentNote.description,
          auditInformation: item.recentNote.auditInformation,
        }
      : undefined,
    history: (item.history ?? []).map((entry) => mapHistory(entry)),
    secondaryProofsAllowed: item.secondaryProofsAllowed,
  };
}

/**
 * Maps the generated `StipulationListResponse` to the panel view model. Counts
 * come from the response (defaulting to 0) rather than being recomputed, since
 * the server may have filtered the item set via `openOnly`.
 * @param {StipulationListResponse} response - The BFF response body.
 * @returns {StipulationListView} The panel-ready list.
 */
export function mapStipulationList(response: StipulationListResponse): StipulationListView {
  return {
    items: (response.stipulations ?? []).map((item, index) => mapStipulationItem(item, index)),
    openCount: response.openCount ?? 0,
    totalCount: response.totalCount ?? 0,
  };
}
